// Geocoding service using our backend API (which proxies to Nominatim)
import $axios from "../http/accessor";
import { locations } from "../endpoints/locations.endpoints";

export interface GeocodingResult {
  display_name: string;
  lat: string;
  lon: string;
  place_id: number;
  address?: {
    house_number?: string;
    road?: string;
    city?: string;
    state?: string;
    country?: string;
    postcode?: string;
  };
}

export interface ReverseGeocodingResult {
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    house_number?: string;
    road?: string;
    city?: string;
    state?: string;
    country?: string;
    postcode?: string;
  };
}

class GeocodingService {
  private lastRequestTime = 0;
  private readonly minRequestInterval = 1000; // 1 second in milliseconds

  // Debounce helper to respect rate limits
  private async rateLimitDelay(): Promise<void> {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;

    if (timeSinceLastRequest < this.minRequestInterval) {
      await new Promise((resolve) =>
        setTimeout(resolve, this.minRequestInterval - timeSinceLastRequest),
      );
    }

    this.lastRequestTime = Date.now();
  }

  // Search for addresses (geocoding)
  async searchAddress(
    query: string,
    limit: number = 5,
    cityName?: string,
    countryName?: string,
  ): Promise<GeocodingResult[]> {
    if (!query || query.trim().length < 3) {
      return [];
    }

    await this.rateLimitDelay();

    try {
      // If city is specified, search within that city
      let searchQuery = query;
      if (cityName && countryName) {
        searchQuery = `${query}, ${cityName}, ${countryName}`;
      } else if (cityName) {
        searchQuery = `${query}, ${cityName}`;
      }

      const response = await $axios.get(locations.geocoding.search, {
        params: {
          q: searchQuery,
          limit: limit,
        },
      });

      return response.data as GeocodingResult[];
    } catch (error) {
      console.error("Geocoding error:", error);
      return [];
    }
  }

  // Get city coordinates and bounds
  async getCityBounds(
    cityName: string,
    countryName?: string,
  ): Promise<{
    center: [number, number];
    bounds: [[number, number], [number, number]] | null;
  } | null> {
    await this.rateLimitDelay();

    try {
      const params: any = {
        city: cityName,
      };
      if (countryName) {
        params.country = countryName;
      }

      const response = await $axios.get(locations.geocoding.cityBounds, {
        params,
      });

      const result = response.data;
      if (result && result.lat && result.lon) {
        const center: [number, number] = [
          parseFloat(result.lat),
          parseFloat(result.lon),
        ];

        // Try to get bounding box from the result
        let bounds: [[number, number], [number, number]] | null = null;
        if (
          result.boundingbox &&
          Array.isArray(result.boundingbox) &&
          result.boundingbox.length === 4
        ) {
          // boundingbox format: [min_lat, max_lat, min_lon, max_lon]
          bounds = [
            [
              parseFloat(result.boundingbox[0]),
              parseFloat(result.boundingbox[2]),
            ], // SW corner
            [
              parseFloat(result.boundingbox[1]),
              parseFloat(result.boundingbox[3]),
            ], // NE corner
          ];
        }

        return { center, bounds };
      }

      return null;
    } catch (error) {
      console.error("City bounds error:", error);
      return null;
    }
  }

  // Convert coordinates to address (reverse geocoding)
  async reverseGeocode(
    lat: number,
    lon: number,
  ): Promise<ReverseGeocodingResult | null> {
    await this.rateLimitDelay();

    try {
      const response = await $axios.get(locations.geocoding.reverse, {
        params: {
          lat: lat.toString(),
          lon: lon.toString(),
        },
      });

      return response.data as ReverseGeocodingResult;
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      return null;
    }
  }

  // Format address from geocoding result
  formatAddress(result: GeocodingResult | ReverseGeocodingResult): string {
    if (result.address) {
      const parts: string[] = [];

      if (result.address.house_number && result.address.road) {
        parts.push(`${result.address.house_number} ${result.address.road}`);
      } else if (result.address.road) {
        parts.push(result.address.road);
      }

      if (result.address.city) {
        parts.push(result.address.city);
      } else if (result.address.state) {
        parts.push(result.address.state);
      }

      if (result.address.postcode) {
        parts.push(result.address.postcode);
      }

      if (result.address.country) {
        parts.push(result.address.country);
      }

      return parts.join(", ") || result.display_name;
    }

    return result.display_name;
  }
}

const geocodingService = new GeocodingService();
export default geocodingService;
